import { Stack, LoadingOverlay } from '@mantine/core';
import Card from 'components/Card';
import DataTables, { DataTableColumn } from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import { useDisclosure } from '@mantine/hooks';
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  assessmentScheduleService,
  AssessmentSchedule
} from 'services/lia/assessmentScheduleService';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import dayjs from 'dayjs';
import { formatLocalDate } from 'utils/convertLocalFormat';
import CopyScheduleModal from './CopyScheduleModal';

const TableScheduleAssessment: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const searchParams = new URLSearchParams(location.search);
  const defaultSearch = searchParams.get('search') || '';

  const [previousSearch, setPreviousSearch] = useState(defaultSearch);

  const [opened, { open, close }] = useDisclosure();
  const [scheduleToDelete, setScheduleToDelete] = useState<string | null>(null);

  const [copyModalOpened, { open: openCopyModal, close: closeCopyModal }] = useDisclosure();
  const [scheduleToCopy, setScheduleToCopy] = useState<AssessmentSchedule | null>(null);

  const [columns, setColumns] = React.useState<DataTableColumn[]>([
    {
      heading: 'Course Name',
      props: 'name',
      sortable: true,
      active: true,
      id: 'name'
    },
    {
      heading: 'Start Schedule Date',
      props: (row: AssessmentSchedule) => formatLocalDate(row.startTime, 'DD MMM YYYY HH:mm'),
      sortable: true,
      active: true,
      id: 'startTime'
    },
    {
      heading: 'End Schedule Date',
      props: (row: AssessmentSchedule) => formatLocalDate(row.endTime, 'DD MMM YYYY HH:mm'),
      sortable: true,
      active: true,
      id: 'endTime'
    },
    {
      heading: 'Type Assessment',
      props: 'typeAssessment',
      sortable: true,
      active: true,
      id: 'typeAssessment'
    },
    {
      heading: 'Created By',
      props: 'userIn',
      sortable: true,
      active: true,
      id: 'userIn'
    },
    {
      heading: 'Created Date',
      props: (row: AssessmentSchedule) => dayjs(row.dateIn).format('DD MMM YYYY HH:mm'),
      sortable: true,
      active: true,
      id: 'dateIn'
    },
    {
      heading: 'Updated By',
      props: (row: AssessmentSchedule) => row.userUp || '-',
      sortable: true,
      active: true,
      id: 'userUp'
    },
    {
      heading: 'Updated Date',
      props: (row: AssessmentSchedule) =>
        row.dateUp ? dayjs(row.dateUp).format('DD MMM YYYY HH:mm') : '-',
      sortable: true,
      active: true,
      id: 'dateUp'
    },
    {
      heading: 'Actions',
      active: true,
      props: 'action'
    }
  ]);

  const {
    data: schedulesData,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['assessmentSchedules', defaultSearch, currentPage, pageSize],
    queryFn: () =>
      assessmentScheduleService.getList({
        search: defaultSearch,
        page: currentPage,
        limit: pageSize
      }),
    refetchOnWindowFocus: false
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => assessmentScheduleService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessmentSchedules'] });
      close();
    }
  });

  const handleView = (data: AssessmentSchedule) => {
    const url = `/app/lia/assessment-schedules/detail/${data.msAssessmentScheduleID}`;
    navigate(url);
  };

  const handleEdit = (data: AssessmentSchedule) => {
    const url = `/app/lia/assessment-schedules/edit/${data.msAssessmentScheduleID}`;
    navigate(url);
  };

  const handleDelete = (data: AssessmentSchedule) => {
    setScheduleToDelete(data.msAssessmentScheduleID);
    open();
  };

  const confirmDelete = async () => {
    if (scheduleToDelete) {
      await deleteMutation.mutateAsync(scheduleToDelete);
    }
  };

  const copyMutation = useMutation({
    mutationFn: (data: { scheduleId: string; copyData: any }) =>
      assessmentScheduleService.copySchedule(data.scheduleId, data.copyData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessmentSchedules'] });
      closeCopyModal();
    }
  });

  const handleCopy = (data: AssessmentSchedule) => {
    setScheduleToCopy(data);
    openCopyModal();
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (defaultSearch !== previousSearch) {
      setCurrentPage(1);
      setPreviousSearch(defaultSearch);
    }
  }, [defaultSearch, previousSearch]);

  useEffect(() => {
    if (schedulesData?.pageNumber && schedulesData.pageNumber !== currentPage) {
      setCurrentPage(schedulesData.pageNumber);
    }
  }, [schedulesData?.pageNumber]);

  useEffect(() => {
    if (schedulesData?.pageSize && schedulesData.pageSize !== pageSize) {
      setPageSize(schedulesData.pageSize);
    }
  }, [schedulesData?.pageSize]);

  const tableData = {
    data: schedulesData?.data || [],
    pageNumber: schedulesData?.pageNumber || 1,
    pageSize: schedulesData?.pageSize || 10,
    totalPages: schedulesData?.totalPages || 0,
    totalRecords: schedulesData?.totalRecords || 0
  };

  return (
    <Card>
      <Stack>
        <ModalPopupConfirmationComponent
          buttonText='Delete'
          statusProcess='delete'
          opened={opened}
          open={open}
          close={close}
          onSubmit={confirmDelete}
        />

        <CopyScheduleModal
          opened={copyModalOpened}
          onClose={closeCopyModal}
          schedule={scheduleToCopy}
          onSubmit={(copyData) => {
            if (scheduleToCopy) {
              copyMutation.mutateAsync({
                scheduleId: scheduleToCopy.msAssessmentScheduleID,
                copyData
              });
            }
          }}
          isLoading={copyMutation.isPending}
        />

        <Toolbar
          columns={columns}
          createButtonText='Schedule Assessment'
          showFilterColumn
          showFilter={false}
          setColumns={setColumns}
          defaultSearchValue={defaultSearch}
          onCreate={() => {
            window.open('/app/lia/assessment-schedules/add', '_blank');
          }}
        />

        <div style={{ position: 'relative' }}>
          <LoadingOverlay
            visible={isLoading || isFetching || deleteMutation.isPending || copyMutation.isPending}
          />
          <DataTables
            columns={columns}
            data={tableData.data}
            loading={isLoading}
            activeSize={pageSize}
            sizing={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 }
            ]}
            withPagination
            totalData={tableData.totalRecords}
            currentPage={currentPage}
            currentSize={pageSize}
            handleChangePage={handlePageChange}
            handleChangeSize={handleSizeChange}
            withBulkData={false}
            handleView={handleView}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleCopy={handleCopy}
            viewText='View Assessment'
            editText='Edit Assessment'
            deleteText='Delete Assessment'
            copyText='Copy Assessment'
            emptyMessageTitle='No Assessment Schedules'
            emptyMessageDescription='Click create button to add new assessment schedule'
          />
        </div>
      </Stack>
    </Card>
  );
};

export default TableScheduleAssessment;
